import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { OtpDialog } from '../components/OtpDialog';
import { SignInShell } from '../components/SignInShell';
import { useAuth } from '../context/useAuth';
import { ApiError } from '../lib/api';
import { OTP_DEMO_CODE, OTP_LENGTH } from '../lib/otp';
import {
  PASSWORD_MAX,
  PASSWORD_RULES,
  RESET_SIMULATED,
  isEmail,
  requestReset,
  resetPassword,
  verifyResetCode,
} from '../lib/passwordReset';

/**
 * email → code → password. Kept in memory on purpose: a reload starts over at
 * the email step, so a half-finished reset never survives on a shared computer.
 */
type Step = 'email' | 'code' | 'password';

export function ForgotPassword() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Someone already signed in changes their password from their profile.
  if (!loading && user) return <Navigate to="/web" replace />;

  const address = email.trim().toLowerCase();
  const rules = PASSWORD_RULES.map((rule) => ({ ...rule, ok: rule.test(password) }));
  const matches = confirm !== '' && confirm === password;
  const canSave = rules.every((rule) => rule.ok) && matches;

  async function onSendCode(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!isEmail(email)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError(null);

    setBusy(true);
    try {
      await requestReset(address);
      setStep('code');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function onVerify(code: string) {
    await verifyResetCode(code);
    setStep('password');
  }

  function onCodeLocked() {
    setStep('email');
    setError('Too many incorrect codes. Please request a new code.');
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (!canSave || busy) return;

    setError(null);
    setBusy(true);
    try {
      await resetPassword(password);
      // Not signed in on purpose: the new password is proven by signing in.
      navigate('/login?reason=password_changed', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
      setBusy(false);
    }
  }

  return (
    <SignInShell
      overlay={
        step === 'code' && (
          <OtpDialog
            email={address}
            title="Check your email"
            description={
              <>
                We sent a {OTP_LENGTH}-digit code to <b>{address}</b>. Enter it to continue.
              </>
            }
            simulatedNote={
              <>
                Email delivery is simulated for now — use <code>{OTP_DEMO_CODE}</code>.
              </>
            }
            onVerify={onVerify}
            onCancel={() => setStep('email')}
            onLocked={onCodeLocked}
          />
        )
      }
    >
      {step !== 'password' ? (
        <form className="signin-card" onSubmit={onSendCode} noValidate>
          <h2 className="signin-card-title">Forgot password</h2>
          <p className="signin-note">
            Enter your user name (email address) and we will send a {OTP_LENGTH}-digit code to it.
          </p>

          {error && (
            <div className="alert" role="alert">
              {error}
            </div>
          )}

          <input
            className={emailError ? 'signin-input signin-input--error' : 'signin-input'}
            type="email"
            name="email"
            placeholder="User Name (email address)"
            aria-label="User Name (email address)"
            aria-invalid={emailError ? true : undefined}
            aria-describedby={emailError ? 'forgot-email-error' : undefined}
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
          {emailError && (
            <p id="forgot-email-error" className="field-error">
              {emailError}
            </p>
          )}

          <div className="signin-actions">
            <button type="submit" className="btn-signin" disabled={busy}>
              {busy ? 'Sending…' : 'Send code'}
            </button>
            <button type="button" className="btn-forgot" onClick={() => navigate('/login')}>
              Back to Sign-in
            </button>
          </div>
        </form>
      ) : (
        <form className="signin-card" onSubmit={onSave} noValidate>
          <h2 className="signin-card-title">Choose a new password</h2>
          <p className="signin-note">
            For <b>{address}</b>.
          </p>

          {error && (
            <div className="alert" role="alert">
              {error}
            </div>
          )}

          <input
            className="signin-input"
            type={show ? 'text' : 'password'}
            name="new-password"
            placeholder="New password"
            aria-label="New password"
            aria-describedby="pw-rules"
            autoComplete="new-password"
            maxLength={PASSWORD_MAX}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
          />

          <input
            className="signin-input"
            type={show ? 'text' : 'password'}
            name="confirm-password"
            placeholder="Confirm new password"
            aria-label="Confirm new password"
            autoComplete="new-password"
            maxLength={PASSWORD_MAX}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />

          <label className="signin-check">
            <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
            <span>Show password</span>
          </label>

          <ul id="pw-rules" className="pw-rules">
            {rules.map((rule) => (
              <Rule key={rule.id} ok={rule.ok} label={rule.label} />
            ))}
            <Rule ok={matches} label="Both passwords match" />
          </ul>

          <div className="signin-actions signin-actions--gated">
            <button type="submit" className="btn-signin" disabled={!canSave || busy}>
              {busy ? 'Saving…' : 'Save password'}
            </button>
            <button type="button" className="btn-forgot" onClick={() => navigate('/login')}>
              Cancel
            </button>
          </div>

          {RESET_SIMULATED && (
            <p className="signin-sim">
              Password reset is simulated for now — nothing is changed on the server.
            </p>
          )}
        </form>
      )}
    </SignInShell>
  );
}

function Rule({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li
      className={ok ? 'pw-rule pw-rule--ok' : 'pw-rule'}
      aria-label={`${label}: ${ok ? 'met' : 'not met'}`}
    >
      <span aria-hidden="true">{ok ? '✓' : '•'}</span>
      {label}
    </li>
  );
}
